import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { KeyRound, Save, UserCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchProfileLookups, hydrateProfile } from '../lib/profileLookups';
import { formatRoleLabel } from '../lib/accessRules';

function AccountSettingsPage() {
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [namaLengkap, setNamaLengkap] = useState('');
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    setAuthUser(user);

    const { data: profileData, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    try {
      const lookups = await fetchProfileLookups(supabase);
      setProfile(hydrateProfile(profileData, lookups));
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    setNamaLengkap(profileData.nama_lengkap || '');
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setSuccessMsg('');
    setErrorMsg('');

    if (!namaLengkap.trim()) {
      setErrorMsg('Nama lengkap wajib diisi.');
      setSavingProfile(false);
      return;
    }

    const { data, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .update({ nama_lengkap: namaLengkap.trim() })
      .eq('id', profile.id)
      .select('*')
      .maybeSingle();

    if (error) {
      setErrorMsg(error.message);
      setSavingProfile(false);
      return;
    }

    setProfile(data);
    setSuccessMsg('Data akun berhasil diperbarui.');
    setSavingProfile(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    setSuccessMsg('');
    setErrorMsg('');

    if (passwordData.newPassword.length < 6) {
      setErrorMsg('Password baru minimal 6 karakter.');
      setSavingPassword(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMsg('Konfirmasi password tidak sama.');
      setSavingPassword(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: passwordData.newPassword,
    });

    if (error) {
      setErrorMsg(error.message);
      setSavingPassword(false);
      return;
    }

    setPasswordData({ newPassword: '', confirmPassword: '' });
    setSuccessMsg('Password berhasil diperbarui.');
    setSavingPassword(false);
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Akun Saya"
        breadcrumbs={['Dashboard', 'Pengaturan', 'Akun Saya']}
        profile={profile}
        activeMenu="settings"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Akun Saya"
      breadcrumbs={['Dashboard', 'Pengaturan', 'Akun Saya']}
      profile={profile}
      activeMenu="settings"
    >
      <Container fluid className="p-0">
        {successMsg && <Alert variant="success">{successMsg}</Alert>}
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Row className="g-4">
          <Col xl={7}>
            <Card className="content-card-v2 settings-card">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Informasi Akun</h4>
                  </div>
                  <div className="settings-menu-icon compact">
                    <UserCircle2 size={22} />
                  </div>
                </div>

                <Form onSubmit={handleSaveProfile}>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Label>Nama Lengkap</Form.Label>
                      <Form.Control
                        value={namaLengkap}
                        onChange={(e) => setNamaLengkap(e.target.value)}
                        required
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Email Auth</Form.Label>
                      <Form.Control value={authUser?.email || '-'} readOnly />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Role</Form.Label>
                      <Form.Control value={formatRoleLabel(profile)} readOnly />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Status Akun</Form.Label>
                      <Form.Control
                        value={profile?.is_active ? 'Aktif' : 'Nonaktif'}
                        readOnly
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Prodi</Form.Label>
                      <Form.Control
                        value={profile?.prodi_display || profile?.prodi || '-'}
                        readOnly
                      />
                    </Col>
                  </Row>

                  <div className="criterion-form-actions">
                    <Button className="save-btn-v2" type="submit" disabled={savingProfile}>
                      <Save size={16} className="me-2" />
                      {savingProfile ? 'Menyimpan...' : 'Simpan Profil'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <Card className="content-card-v2 settings-card">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Ganti Password</h4>
                  </div>
                  <div className="settings-menu-icon compact">
                    <KeyRound size={22} />
                  </div>
                </div>

                <Form onSubmit={handleChangePassword}>
                  <Form.Group className="mb-3">
                    <Form.Label>Password Baru</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Konfirmasi Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>

                  <div className="criterion-form-actions">
                    <Button className="save-btn-v2" type="submit" disabled={savingPassword}>
                      <KeyRound size={16} className="me-2" />
                      {savingPassword ? 'Menyimpan...' : 'Update Password'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
}

export default AccountSettingsPage;
